import {
  createFrontendModule,
  createRouteRef,
  PageBlueprint,
  coreExtensionData,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import { jsx, Fragment } from 'react/jsx-runtime';
import CategoryIcon from '@material-ui/icons/Category';
import { catalogTableColumnsFunc } from './catalog-table-columns-func';

// Overrides plugin-catalog's own page:catalog extension - a no-name
// makeWithOverrides() registered under pluginId 'catalog' produces the same
// id `page:catalog` the stock page registers under, so this replaces it.
//
// Unlike modules/signIn/SignInPage.tsx (which fully replaces its target with
// make()), this one calls the ORIGINAL factory rather than building the page
// from scratch: the first attempt here used make() and silently dropped the
// `filters` input, which the eight catalog-filter:catalog/* extensions
// (kind, type, lifecycle, tag, ...) attach to - breaking the entire page,
// not just the missing column. Re-declaring the same `filters` input here
// and threading it through originalFactory keeps that wiring intact.
//
// `exportConfig` (the stock extension's other input) is deliberately not
// re-declared - its data ref isn't exported publicly, and nothing in this
// app uses CatalogExportConfigBlueprint, so there's nothing that would ever
// attach to it. Same reasoning for not passing `pagination`/`exportSettings`
// through app-config.yaml: only `path` is configured today, and that's a
// PageBlueprint-level field handled automatically regardless of this
// override.
//
// The stock extension also passes a `routeRef` (its own module-private
// `rootRouteRef`, id "catalog") - not exported by @backstage/plugin-catalog
// under either the "." or "./alpha" entrypoints, so it can't be imported
// here without a fragile deep import into the package's dist internals.
// AppNav's sidebar renderer (@backstage/plugin-app's AppNav.esm.js) drops a
// page node from `navItems` entirely when it has no routeRef - this runs
// BEFORE the title/icon check below, so a page missing routeRef never shows
// in the sidebar regardless of title/icon. Fixed by minting our own
// routeRef here (same id "catalog", so anything reading `.id` for
// diagnostics sees the same value) - nothing in this app declares an
// external-route-ref binding targeting the catalog plugin's root route, so
// there's no other consumer that needs the exact stock instance. (The new
// frontend system's createRouteRef takes no `id` - one is assigned
// internally once this ref is bound to the page:catalog node below.)
const catalogRootRouteRef = createRouteRef();
const catalogPage = PageBlueprint.makeWithOverrides({
  inputs: {
    filters: createExtensionInput([coreExtensionData.reactElement]),
  },
  factory(originalFactory, { inputs }) {
    return originalFactory({
      path: '/catalog',
      routeRef: catalogRootRouteRef,
      // The stock factory sets these too (title: "Catalog", the same
      // CategoryIcon) - dropped by mistake in the original version of this
      // override, which silently removed the Catalog link from the sidebar:
      // PageBlueprint only emits core.title/core.icon when both are
      // supplied, and the Sidebar renders nav.rest() over whatever nav items
      // exist rather than synthesising one from a bare route (same mechanism
      // noted on secretsOverviewPage in plugin.tsx). The page was still
      // reachable by URL the whole time, just invisible in the sidebar.
      icon: jsx(CategoryIcon, { fontSize: 'inherit' }),
      title: 'Catalog',
      loader: async () => {
        // The alpha entrypoint's CatalogIndexPage is the bare component the
        // stock extension itself renders - the non-alpha one wraps it as a
        // legacy routable extension (createRoutableExtension), which throws
        // "was not discovered in the app element tree" when nested inside a
        // new-frontend-system page loader instead of mounted as a route
        // directly.
        const { CatalogIndexPage } = await import(
          '@backstage/plugin-catalog/alpha'
        );
        const filters = inputs.filters.map(filter =>
          filter.get(coreExtensionData.reactElement),
        );
        return jsx(CatalogIndexPage, {
          filters: jsx(Fragment, { children: filters }),
          pagination: true,
          columns: catalogTableColumnsFunc,
        });
      },
    });
  },
});

export const catalogColumnsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [catalogPage],
});
